import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Briefcase,
  Clock,
  CheckCircle,
  TrendingUp,
  Users,
  Plus,
  ArrowRight,
  AlertCircle,
  DollarSign,
  Calendar,
  FileText
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientDashboard() {
  const { token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    active: 0,
    completed: 0,
    totalSpent: 0
  });
  const [recentProjects, setRecentProjects] = useState([]);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, [token]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch projects
      const projectsRes = await fetch(`${API_URL}/api/projects-v3/client/my-projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (projectsRes.ok) {
        const projects = await projectsRes.json();
        
        // Calculate stats
        const statsData = {
          total: projects.length,
          pending: projects.filter(p => ["pending_holding_review", "approved_pending_client_sign"].includes(p.status)).length,
          active: projects.filter(p => ["live", "has_applications", "team_selected", "in_progress"].includes(p.status)).length,
          completed: projects.filter(p => p.status === "completed").length,
          totalSpent: projects.reduce((sum, p) => sum + (p.final_budget || p.proposed_budget || 0), 0)
        };
        
        setStats(statsData);
        setRecentProjects(projects.slice(0, 5)); // Latest 5 projects
      }

      // Fetch notifications
      const notifRes = await fetch(`${API_URL}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (notifRes.ok) {
        const notifs = await notifRes.json();
        setNotifications(notifs.slice(0, 5)); // Latest 5 notifications
      }
      
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Kunne ikke laste dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending_holding_review: "bg-yellow-500/20 text-yellow-400",
      approved_pending_client_sign: "bg-blue-500/20 text-blue-400",
      live: "bg-green-500/20 text-green-400",
      has_applications: "bg-purple-500/20 text-purple-400",
      team_selected: "bg-indigo-500/20 text-indigo-400",
      in_progress: "bg-cyan-500/20 text-cyan-400",
      completed: "bg-emerald-500/20 text-emerald-400"
    };
    return colors[status] || "bg-gray-500/20 text-gray-400";
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("no-NO", {
      style: "currency",
      currency: "NOK",
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <DashboardLayout title={t('nav.dashboard')}>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={t('nav.dashboard')}>
      <div className="space-y-6 p-6">
        {/* Welcome Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">{t('dashboard.welcome')}</h1>
            <p className="text-gray-400 mt-1">{t('dashboard.overview')}</p>
          </div>
          <Link to="/client/request-project">
            <Button className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black">
              <Plus className="w-4 h-4 mr-2" />
              {t('projects.newProject')}
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">{t('dashboard.totalProjects')}</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.total}</p>
                </div>
                <div className="p-3 bg-blue-500/20 rounded-lg">
                  <Briefcase className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">{t('dashboard.pending')}</p>
                  <p className="text-3xl font-bold text-yellow-400 mt-1">{stats.pending}</p>
                </div>
                <div className="p-3 bg-yellow-500/20 rounded-lg">
                  <Clock className="w-6 h-6 text-yellow-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">{t('dashboard.active')}</p>
                  <p className="text-3xl font-bold text-green-400 mt-1">{stats.active}</p>
                </div>
                <div className="p-3 bg-green-500/20 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">{t('dashboard.totalSpent')}</p>
                  <p className="text-2xl font-bold text-[#23F7DD] mt-1">{formatCurrency(stats.totalSpent)}</p>
                </div>
                <div className="p-3 bg-[#23F7DD]/20 rounded-lg">
                  <DollarSign className="w-6 h-6 text-[#23F7DD]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
          <CardHeader>
            <CardTitle className="text-white">{t('dashboard.quickActions')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link to="/client/request-project">
                <Button variant="outline" className="w-full border-[#2a2a4a] text-white hover:bg-[#2a2a4a]">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('dashboard.createNewProject')}
                </Button>
              </Link>
              <Link to="/client/projects-v3">
                <Button variant="outline" className="w-full border-[#2a2a4a] text-white hover:bg-[#2a2a4a]">
                  <Briefcase className="w-4 h-4 mr-2" />
                  {t('dashboard.viewAllProjects')}
                </Button>
              </Link>
              <Link to="/client/contracts">
                <Button variant="outline" className="w-full border-[#2a2a4a] text-white hover:bg-[#2a2a4a]">
                  <FileText className="w-4 h-4 mr-2" />
                  {t('dashboard.viewContracts')}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Projects */}
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">{t('dashboard.recentProjects')}</CardTitle>
              <Link to="/client/projects-v3">
                <Button variant="ghost" size="sm" className="text-[#23F7DD]">
                  {t('common.viewAll')} <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {recentProjects.length === 0 ? (
                <div className="text-center py-8">
                  <Briefcase className="w-10 h-10 mx-auto text-gray-600 mb-3" />
                  <p className="text-gray-400">{t('dashboard.noProjects')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentProjects.map((project) => (
                    <Link 
                      key={project.project_id} 
                      to={`/client/projects-v3/${project.project_id}`}
                      className="block p-3 bg-[#0f0f1a] rounded-lg border border-[#2a2a4a] hover:border-[#23F7DD]/50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{project.title}</p>
                          <p className="text-gray-500 text-sm">{project.location_city}</p>
                        </div>
                        <Badge className={`ml-2 ${getStatusColor(project.status)} border-0`}>
                          {project.status}
                        </Badge>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Activity / Notifications */}
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardHeader>
              <CardTitle className="text-white">{t('dashboard.recentActivity')}</CardTitle>
            </CardHeader>
            <CardContent>
              {notifications.length === 0 ? (
                <div className="text-center py-8">
                  <AlertCircle className="w-10 h-10 mx-auto text-gray-600 mb-3" />
                  <p className="text-gray-400">{t('dashboard.noActivity')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notifications.map((notif) => (
                    <div 
                      key={notif.notification_id}
                      className={`p-3 rounded-lg border ${
                        !notif.read 
                          ? "bg-[#23F7DD]/5 border-[#23F7DD]/30" 
                          : "bg-[#0f0f1a] border-[#2a2a4a]"
                      }`}
                    >
                      <p className={`text-sm ${!notif.read ? "text-white font-medium" : "text-gray-300"}`}>
                        {notif.title}
                      </p>
                      <p className="text-gray-500 text-xs mt-1">
                        {new Date(notif.created_at).toLocaleString("no-NO", {
                          day: "2-digit",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit"
                        })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
